import type { DataContextValues,
    DateInterval,
    InputBudget,
    InputRates,
    WorkloadData,
    DefaultContributions,
    CustomContributions,
    PAData,
    OrgData, Modulation } from 'types/dataContext';
import type { ConfigurationData } from 'types/api';
import React, { createContext, useState, useMemo, useEffect, useCallback } from 'react';
import { API } from 'services';
import { NoScenario, Scenario, ScenarioList } from 'types/scenario';
import useAuth from 'hooks/useAuth';
import type { Coworker } from 'types/coworker';
import { APIPath } from 'components/CapacityFactor/FileUpload/types';
import { useToast } from 'hooks/useToast';
import { orgDataTransformation, updatePAData, updateScenarioAttributes } from './DataContext.utils';

const DataContext = createContext<DataContextValues>({
    config: undefined,
    coworkerData: undefined,
    currentScenario: undefined,
    currentCountry: undefined,
    currentUnit: undefined,
    currentUnitType: undefined,
    currentUnitBudget: undefined,
    currentUnitRates: undefined,
    defaultContributions: undefined,
    customContributions: undefined,
    isFetching: false,
    getScenarios: () => { },
    scenarioList: undefined,
    setScenarioList: () => { },
    selectCountry: () => { },
    selectScenario: () => { },
    selectUnit: () => { },
    scenarioBudget: false,
    setScenarioBudget: () => { },
    setCurrentScenario: () => { },
    setDateInterval: () => { },
    orgData: undefined,
    workload: undefined,
    getUploadedFileData: () => { },
    modulationData: undefined,
});

/**
 * React Context used as a data layer
 */
const DataProvider = ({ children }: { children: JSX.Element }) => {
    const [orgData, setOrgData] = useState<OrgData | undefined>();
    const [coworkerData, setCoworkerData] = useState<Array<Coworker> | undefined>();
    const [currentCountry, setCurrentCountry] = useState<string | undefined>();
    const [currentUnit, setCurrentUnit] = useState<string | undefined>(undefined);
    const [currentUnitType, setCurrentUnitType] = useState<string | undefined>(undefined);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [currentUnitBudget, setCurrentUnitBudget] = useState<InputBudget | undefined>();
    const [scenarioBudget, setScenarioBudget] = useState<boolean>(false);
    const [currentUnitRates, setCurrentUnitRates] = useState<InputRates>();
    const [scenarioList, setScenarioList] = useState<undefined | ScenarioList>(undefined);
    const [currentScenario, setCurrentScenario] = useState<undefined | Scenario>(undefined);
    const [defaultContributions, setDefaultContributions] = useState<DefaultContributions>();
    const [customContributions, setCustomContributions] = useState<CustomContributions>();
    const [workload, setWorkload] = useState<WorkloadData>();
    const { access } = useAuth();
    const { displayToast } = useToast();
    // dateInterval will be set when timeSelecion is updated in AppContext
    const [dateInterval, setDateInterval] = useState<DateInterval>();
    const [config, setConfig] = useState<ConfigurationData>();
    const [modulationData, setModulationData] = useState<Modulation | undefined>();
    /**
     * Gets the unit-level data which is dependent on the time selection
     */
    const getUnitData = async (unitId: string, interval: DateInterval) => {
        // selectUnit requires unitType to be provided. A valid unitType to correctly match the right business unit.
        const currentunitId = orgData?.businessUnits?.find(({ code, type }) => code === unitId && type === currentUnitType)?.id;
        if (access?.api?.readInputs) {
            setIsFetching(true);
            const pUnitBudget = API().getBudget(
                currentCountry,
                currentUnit,
                currentUnitType,
                interval.startDate,
                interval.endDate,
                scenarioBudget ? currentScenario?.id : undefined
            );
            const pModulationData = access?.features?.modulation(currentCountry)
                ? API().getModulation(currentCountry, currentunitId, interval?.startDate, interval?.endDate)
                : Promise.resolve({});
            const pUnitRates = API().getRates(currentCountry, unitId, currentUnitType, interval.startDate, interval.endDate);
            const pUnitWorkload = API().getWorkload(currentCountry, unitId, currentUnitType, interval?.startDate, interval?.endDate);
            const paData = API().getPAData(currentCountry, currentUnit, currentUnitType, interval?.startDate, interval?.endDate);

            Promise.allSettled([pModulationData, pUnitRates, pUnitBudget, pUnitWorkload, paData]).then(values => {
                const [modulationDataResp, pRates, pBudget, pWorkload, personalAdminData] = values;
                values.forEach(value => {
                    if (value.status === 'rejected') {
                        displayToast({ title: 'ERROR', message: value.reason.message });
                    }
                });
                setModulationData((modulationDataResp as PromiseFulfilledResult<Modulation>).value);
                setCurrentUnitRates((pRates as PromiseFulfilledResult<{ data: InputRates }>).value?.data);
                setCurrentUnitBudget((pBudget as PromiseFulfilledResult<InputBudget>).value);
                setWorkload((pWorkload as PromiseFulfilledResult<{ data: { workload : WorkloadData } }>).value?.data?.workload);
                const paDataResponse = (personalAdminData as PromiseFulfilledResult<PAData>).value;
                if (paDataResponse?.coworkers?.length) {
                    setCoworkerData(updatePAData(paDataResponse, orgData));
                }
            }).finally(() => {
                setTimeout(() => {
                    setIsFetching(false);
                }, 700);
            });
        }
    };

    // Refresh/fetch latest data from uploaded file and auto update grid by refresh
    const getUploadedFileData = useCallback(async (fileCategory: string): Promise<void> => {
        if (currentCountry
            && currentUnit
            && currentUnitType
            && fileCategory
            && dateInterval?.startDate
            && dateInterval?.endDate
            && access?.api.readPAData
            && access?.api?.readInputs) {
            setIsFetching(true);
            if (fileCategory === APIPath.DEFAULT_CONFIG || fileCategory === APIPath.CUSTOM_CONTRIBUTIONS) {
                const paData: PAData = await API().getPAData(currentCountry, currentUnit, currentUnitType, dateInterval.startDate, dateInterval.endDate);
                if (paData?.coworkers?.length) {
                    setCoworkerData(updatePAData(paData, orgData));
                }
            }
            API().getUpdatedFileData(
                currentCountry,
                currentUnit,
                currentUnitType,
                fileCategory,
                dateInterval?.startDate,
                dateInterval?.endDate,
                scenarioBudget ? currentScenario?.id : undefined
            )
                .then(response => {
                    switch (fileCategory) {
                    case APIPath.CAPACITY_FACTORS:
                        setCurrentUnitRates(response.data);
                        break;
                    case APIPath.WORKLOAD:
                        setWorkload(response.data.workload);
                        break;
                    case APIPath.BUDGET_HOURS:
                        setCurrentUnitBudget(response);
                        break;
                    case APIPath.CUSTOM_CONTRIBUTIONS:
                        setCustomContributions(response);
                        break;
                    default:
                        setDefaultContributions(response);
                    }
                }).catch(err => {
                    displayToast({ title: 'ERROR', message: err.message });
                })
                .finally(() => setTimeout(() => {
                    setIsFetching(false);
                }, 700));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCountry, currentUnitType, currentUnit, dateInterval, currentScenario, scenarioBudget]);

    /**
     * Get time range dependent data for units
     */
    useEffect(() => {
        if (currentUnit && dateInterval?.startDate && dateInterval?.endDate) {
            getUnitData(currentUnit, dateInterval);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUnit, currentUnitType, dateInterval]);

    const getScenarios = useCallback((unit: string | undefined, unitType: string | undefined) => {
        if (!currentCountry || !unit || !unitType || !access?.api?.readScenariosList) {
            return;
        }
        setIsFetching(true);
        API().getScenarios(currentCountry, unit, unitType)
            .then(response => setScenarioList(response))
            .catch(err => {
                displayToast({ title: 'ERROR', message: err.message });
                setScenarioList(undefined);
            })
            .finally(() => {
                setIsFetching(false);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCountry, currentUnit, currentUnitType]);

    const selectUnit = useCallback(async (unitId: string, unitType: string): Promise<void> => {
        setCoworkerData(undefined);
        setCurrentScenario(undefined);
        setIsFetching(true);
        setCurrentUnit(unitId);
        setCurrentUnitType(unitType);

        getScenarios(unitId, unitType); // Fetch scenarios for the selected unit
        const pDefaultContrib = access?.api?.readInputs ? API().getDefaultContribution(currentCountry, unitId, unitType) : Promise.resolve({});
        const pCustomContrib = access?.api?.readInputs ? API().getCustomContributions(currentCountry, unitId, unitType) : Promise.resolve({});

        Promise.allSettled([pDefaultContrib, pCustomContrib]).then(values => {
            const [defaultContrib, customContrib] = values;
            values.forEach(value => {
                if (value.status === 'rejected') {
                    displayToast({ title: 'ERROR', message: value.reason.message });
                }
            });
            setDefaultContributions((defaultContrib as PromiseFulfilledResult<DefaultContributions>).value);
            setCustomContributions((customContrib as PromiseFulfilledResult<CustomContributions>).value);
        }).finally(() => {
            setTimeout(() => {
                setIsFetching(false);
            }, 700);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access?.api?.readInputs, access?.api.readPAData, access?.api.readScenarios, currentCountry, displayToast]);

    const selectCountry = useCallback((countryCode: string) => {
        setCoworkerData(undefined);
        setOrgData(undefined);
        setCurrentUnit(undefined);
        setScenarioList(undefined);
        setCurrentScenario(undefined);
        setCurrentCountry(countryCode);
        if (access?.api?.readOMData) {
            setIsFetching(true);
            API()
                .getOrgData(countryCode)
                .then(data => {
                    setOrgData(orgDataTransformation(data));
                })
                .catch(err => {
                    displayToast({ title: 'ERROR', message: err.message });
                    setOrgData(undefined);
                })
                .finally(() => {
                    setIsFetching(false);
                });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access?.api?.readOMData]);

    // Runs only when we toggle the Enable scenario based Budget Upload
    useEffect(() => {
        const { startDate, endDate } = dateInterval ?? {};
        if (!currentCountry || !currentUnit || !currentUnitType || !startDate || !endDate) {
            return;
        }
        if (access?.api?.readInputs) {
            setIsFetching(true);
            API().getBudget(
                currentCountry,
                currentUnit,
                currentUnitType,
                startDate,
                endDate,
                scenarioBudget ? currentScenario?.id : undefined
            )
                .then(response => setCurrentUnitBudget(response))
                .catch(err => {
                    displayToast({ title: 'ERROR', message: err.message });
                    setCurrentUnitBudget(undefined);
                })
                .finally(() => {
                    setIsFetching(false);
                });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scenarioBudget]);

    const selectScenario = useCallback((scenarioId: number) => {
        if (scenarioId === NoScenario) {
            setCurrentScenario(undefined);
        } else if (access?.api?.readScenarios) {
            API().getScenario(scenarioId, currentCountry, currentUnit, currentUnitType)
                .then(response => {
                    setCurrentScenario(updateScenarioAttributes(response));
                })
                .catch(err => {
                    displayToast({ title: 'ERROR', message: err.message });
                    setCurrentScenario(undefined);
                });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCountry, currentUnit, currentUnitType]);

    useEffect(() => {
        if (currentCountry && currentUnit && currentUnitType && access?.api?.readConfig) {
            API().getConfig(currentCountry, currentUnit, currentUnitType)
                .then(response => {
                    setConfig(response);
                })
                .catch(err => {
                    displayToast({ title: 'ERROR', message: err.message });
                    setConfig(undefined);
                });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCountry, currentUnit, currentUnitType]);

    const contextValue = useMemo(
        () => ({
            config,
            coworkerData,
            currentScenario,
            currentUnitBudget,
            currentUnitRates,
            setCurrentScenario,
            currentCountry,
            currentUnit,
            currentUnitType,
            defaultContributions,
            customContributions,
            isFetching,
            getScenarios,
            orgData,
            scenarioList,
            setScenarioList,
            selectCountry,
            selectScenario,
            selectUnit,
            scenarioBudget,
            setScenarioBudget,
            setDateInterval,
            workload,
            getUploadedFileData,
            modulationData,
        }),
        [
            config,
            coworkerData,
            isFetching,
            currentCountry,
            currentScenario,
            currentUnitBudget,
            currentUnitRates,
            currentUnit,
            currentUnitType,
            defaultContributions,
            customContributions,
            getScenarios,
            orgData,
            scenarioList,
            setScenarioList,
            selectCountry,
            selectScenario,
            selectUnit,
            scenarioBudget,
            setScenarioBudget,
            setDateInterval,
            workload,
            getUploadedFileData,
            modulationData,
        ],
    );

    return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
};

export { DataContext, DataProvider };
